import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Eye } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { boardMembers as mockBoardMembers, stats } from '../../data/mock';
import { useContent } from '../../context/ContentContext';
import DogwoodIcon from '../ui/DogwoodIcon';
import api from '../../lib/api';

const AboutPage = () => {
  const { c } = useContent();
  const [boardMembers, setBoardMembers] = React.useState(mockBoardMembers);
  React.useEffect(() => {
    api.get('/board-members').then((res) => { if (res.data?.length) setBoardMembers(res.data); }).catch(() => {});
  }, []);

  const values = [1, 2, 3, 4, 5].map((i) => ({
    title: c(`about.value${i}.title`, ['Empowerment', 'Leadership', 'Inclusion', 'Service', 'Integrity'][i - 1]),
    description: c(`about.value${i}.description`, [
      "Supporting every woman's journey to her truest self",
      'Cultivating confident, compassionate community leaders',
      'Welcoming women of all backgrounds, ages, and experiences',
      'Advancing our community through meaningful action',
      'Operating with transparency and nonpartisan commitment',
    ][i - 1]),
  }));

  return (
    <main className="pt-52">
      {/* Hero Section */}
      <section className="py-20 bg-[#f1e3eb]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#bd8cae] text-sm uppercase tracking-wider font-medium">
                {c('about.hero.eyebrow', 'About Us')}
              </span>
              <h1 className="font-serif text-4xl sm:text-5xl text-[#3c4a2a] mt-3 mb-6">
                {c('about.hero.title', 'Building a Stronger Lincoln County, Together')}
              </h1>
              <p className="text-lg text-[#3c4a2a]/80 leading-relaxed">
                {c('about.hero.text', "The Women's Club of Lincoln County is founded on the belief that when women lead, communities thrive. We are a diverse group of women united by our commitment to service, leadership, and empowerment.")}
              </p>
            </div>
            <div className="relative">
              <div className="aspect-[3/2] rounded-2xl overflow-hidden shadow-xl">
                <img
                  src={c('about.hero.image', 'https://customer-assets-7cd3h4nn.emergentagent.net/job_fc3829d4-353f-4fc4-b817-cd1ab3ff1600/artifacts/3jltqyzh_image.webp')}
                  alt="Historic Lincoln County Courthouse in Lincolnton, North Carolina"
                  className="w-full h-full object-cover"
                  data-testid="about-hero-image"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-[#bd8cae]/30 rounded-full -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Mission */}
            <div className="bg-[#f1e3eb] rounded-2xl p-8 lg:p-12">
              <div className="w-14 h-14 rounded-full bg-[#5d6b3c]/10 flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-[#5d6b3c]" />
              </div>
              <h2 className="font-serif text-2xl text-[#3c4a2a] mb-4">Our Mission</h2>
              <p className="text-[#3c4a2a]/80 leading-relaxed text-lg">
                {c('about.mission.text', "To cultivate community advancement through women's leadership while empowering every woman to grow into her truest self.")}
              </p>
            </div>

            {/* Vision */}
            <div className="bg-gradient-to-br from-[#bd8cae]/30 to-[#bd8cae]/10 rounded-2xl p-8 lg:p-12">
              <div className="w-14 h-14 rounded-full bg-[#bd8cae]/10 flex items-center justify-center mb-6">
                <Eye className="w-7 h-7 text-[#bd8cae]" />
              </div>
              <h2 className="font-serif text-2xl text-[#3c4a2a] mb-4">Our Vision</h2>
              <p className="text-[#3c4a2a]/80 leading-relaxed text-lg">
                {c('about.vision.text', "A Lincoln County where every woman has the opportunity, resources, and support to lead authentically and contribute meaningfully to our community's growth and wellbeing.")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 bg-[#f1e3eb]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden shadow-xl">
                <img
                  src={c('about.story.image', 'https://static.prod-images.emergentagent.com/jobs/fc3829d4-353f-4fc4-b817-cd1ab3ff1600/images/b5a3c3da5733d18f5936bb814f15171e2ff093ca7299683532560084ec0a1868.jpeg')}
                  alt="Woman speaking to a group of women about leadership"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-8 -right-8 w-40 h-40 bg-[#9dad84]/30 rounded-full -z-10" />
            </div>
            <div>
              <span className="text-[#bd8cae] text-sm uppercase tracking-wider font-medium">
                Our Story
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl text-[#3c4a2a] mt-3 mb-6">
                {c('about.story.heading', "Founded on Faith in Women's Leadership")}
              </h2>
              <div className="space-y-4 text-[#3c4a2a]/80 leading-relaxed">
                <p>{c('about.story.p1', "The Women's Club of Lincoln County begins with a simple yet powerful idea: that women of all ages, backgrounds, and experiences have unique gifts to offer our community.")}</p>
                <p>{c('about.story.p2', "Our founding members recognize that while women make tremendous contributions across Lincoln County, there isn't a unified space to cultivate leadership, share resources, and amplify impact.")}</p>
                <p>{c('about.story.p3', 'Today, our founding vision is to create opportunities for women to grow, lead, and serve while building bridges across generations and backgrounds.')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-[#bd8cae] text-sm uppercase tracking-wider font-medium">
              What We Stand For
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#3c4a2a] mt-3 mb-4">
              {c('about.values.heading', 'Our Core Values')}
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {values.map((value) => (
              <Card
                key={value.title}
                className="bg-[#f1e3eb] border-0 text-center p-6 hover:shadow-lg transition-shadow"
              >
                <CardContent className="p-0">
                  <div className="w-12 h-12 rounded-full bg-[#bd8cae]/50 flex items-center justify-center mx-auto mb-4">
                    <DogwoodIcon className="w-7 h-7 text-[#5d6b3c]" />
                  </div>
                  <h3 className="font-serif text-lg text-[#3c4a2a] mb-2">
                    {value.title}
                  </h3>
                  <p className="text-sm text-[#3c4a2a]/70">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Nonpartisan Statement */}
      <section className="py-16 bg-[#5d6b3c]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="font-serif text-2xl text-white mb-4">
            {c('about.nonpartisan.heading', 'A Nonpartisan Commitment')}
          </h3>
          <p className="text-white/90 leading-relaxed">
            {c('about.nonpartisan.text', "The Women's Club of Lincoln County is a strictly nonpartisan organization. We welcome women of all political beliefs and do not endorse candidates or political parties. Our focus is on community advancement through service, leadership development, and empowerment—values that transcend political boundaries.")}
          </p>
        </div>
      </section>

      {/* Leadership Section */}
      <section className="py-20 bg-[#f1e3eb]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-[#bd8cae] text-sm uppercase tracking-wider font-medium">
              Our Leadership
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#3c4a2a] mt-3 mb-4">
              Board of Directors
            </h2>
            <p className="text-[#3c4a2a]/70">
              {c('about.board.text', "We're building our founding board. Meet our founding organizer — and explore the open positions waiting for women ready to lead.")}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {boardMembers.map((member) => (
              <Card
                key={member.id}
                className="bg-white border-0 shadow-md overflow-hidden group hover:shadow-xl transition-shadow"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="font-serif text-xl text-[#3c4a2a] mb-1">
                    {member.name}
                  </h3>
                  <p className="text-[#5d6b3c] text-sm font-medium mb-3">
                    {member.title}
                  </p>
                  <p className="text-[#3c4a2a]/70 text-sm">
                    {member.bio}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl text-[#3c4a2a]">The Road Ahead</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="font-serif text-4xl sm:text-5xl text-[#5d6b3c] mb-2">
                  {stat.value}
                </p>
                <p className="text-[#3c4a2a]/70 text-sm uppercase tracking-wider">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#bd8cae]/30 to-[#bd8cae]/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl text-[#3c4a2a] mb-6">
            {c('about.cta.heading', 'Join Our Community of Leaders')}
          </h2>
          <p className="text-[#3c4a2a]/80 text-lg mb-8">
            {c('about.cta.text', "Whether you're ready to lead, learn, or serve, there's a place for you here.")}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/membership">
              <Button className="w-full sm:w-auto bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white px-8 py-6 text-lg">
                Become a Member
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button
                variant="outline"
                className="w-full sm:w-auto border-[#5d6b3c] text-[#5d6b3c] hover:bg-[#5d6b3c] hover:text-white px-8 py-6 text-lg"
              >
                Get in Touch
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
